// Which paragraphs a reader actually read, and which ones they came back to.
// The client half of `convex/reading.ts`.
//
// THIS IS THE FIRST THING THAT MAKES ANONYMOUS READING TOUCH CONVEX. The
// reading path was deliberately static — the corpus is fetched from the CDN and
// the reader never needed a session (`src/lib/corpus.ts`) — and the price of
// answering "where do readers stop" is one small mutation every few seconds
// from every open chapter. It is worth stating plainly rather than discovering
// later: the reading surface now has a backend dependency it did not have, and
// it is written so that dependency can fail without the reader noticing
// (`record` rejections are swallowed; nothing renders off this hook's state).
//
// A VIEW IS DWELL, NOT AN INTERSECTION. Counting `isIntersecting` alone would
// count every paragraph a fast scroll flies past, so "blocks read" would
// measure scroll velocity. A block counts once it has been at least half on
// screen for `DWELL_MS`; it becomes countable AGAIN only after leaving the
// viewport, which is what makes a second count a genuine RE-read rather than a
// long sit.
//
// ONE INTERVAL, NOT ONE TIMER PER BLOCK. A chapter is a few hundred paragraphs;
// per-block `setTimeout`s would mean hundreds of live timers being created and
// cleared on every scroll. The observer only records WHEN a block became
// visible, and a single tick decides what that adds up to.

import { useEffect, useMemo, useRef } from "react";
import { useMutation } from "convex/react";
import { api } from "@bindery/convex/_generated/api";
import { getSessionId } from "@/logging";
import { useLoggerContext } from "@/logging";

/** Time a block must stay at least half-visible before it counts as read. */
const DWELL_MS = 1_000;
/** How often the dwell tick runs. Finer than `DWELL_MS` so a block that is
 *  scrolled past just after qualifying still gets counted. */
const TICK_MS = 500;
/** How often pending counts are sent. Slow enough that a reader scrolling
 *  steadily produces a handful of writes per chapter, not one per paragraph. */
const FLUSH_MS = 10_000;
/** Fraction of a block that must be on screen to be "being read". */
const VISIBLE_RATIO = 0.5;

/**
 * Thresholds the observer reports at. Several, not one, because
 * `intersectionRatio` is measured against the BLOCK's own area: a paragraph
 * taller than twice the reading column can never reach 0.5 of itself no matter
 * how completely it fills the screen, so a single 0.5 threshold means such a
 * block never fires an intersecting entry at all — it reads in the drill-down
 * as a paragraph every reader skips, forever, and it holds `furthestIndex` at
 * the block before it. Reachable at the 28px maximum text size on a phone,
 * which is a low-vision reader's setting (review, sales branch).
 */
const THRESHOLDS = [0, 0.1, 0.25, VISIBLE_RATIO];

/** The unit key a chapter number or matter slug becomes on the wire — the same
 *  shape `unitSegment` produces, so admin and reader agree on what a unit is. */
export type UnitKey = string;

interface Pending {
  /** Views observed since the last flush, per block id. */
  views: Map<string, number>;
  /** Deepest block index reached since the last flush; -1 for none. */
  furthest: number;
}

function emptyPending(): Pending {
  return { views: new Map(), furthest: -1 };
}

/**
 * What the observer knows about blocks currently on screen. Held in a REF, not
 * inside the effect, because the effect re-binds when the retelling column is
 * replaced (the paned↔interleaved swap, an orientation change) and the reader
 * has not moved: rebuilding `countedThisVisit` empty made every paragraph still
 * on screen count a SECOND time a second later, without ever having left the
 * viewport. Re-read rate — the column that exists to evaluate the original-text
 * feature — was inflated by exactly the readers who used it (review, sales
 * branch).
 */
interface VisitState {
  /** When a block became at least half visible; absent = not visible now. */
  visibleSince: Map<string, number>;
  /** Blocks already counted for their current visit. Cleared on exit, which is
   *  what makes the next count a re-read. */
  countedThisVisit: Set<string>;
}

/**
 * Count reads and re-reads of the retelling blocks inside `containerRef`, and
 * flush them to Convex.
 *
 * `columnKey` exists because the retelling column is rendered by two different
 * components (paned vs interleaved) that occupy the same slot: when it changes,
 * `containerRef.current` is a NEW node and the observer has to re-bind or it
 * keeps watching a detached one.
 */
export function useReadingTelemetry({
  containerRef,
  bookSlug,
  unitKey,
  blockIds,
  columnKey,
}: {
  containerRef: React.RefObject<HTMLElement | null>;
  bookSlug: string;
  unitKey: UnitKey;
  /** Every block in the unit, in document order — the index basis for
   *  "furthest reached" and the completion denominator. */
  blockIds: readonly string[];
  columnKey: string;
}): void {
  const record = useMutation(api.reading.record);
  const pending = useRef<Pending>(emptyPending());
  const visit = useRef<VisitState>({
    visibleSince: new Map(),
    countedThisVisit: new Set(),
  });
  // The mutation identity is stable per client, but keeping it in a ref means
  // the observer effect below never re-binds because of it.
  const recordRef = useRef(record);
  useEffect(() => {
    recordRef.current = record;
  });

  // The attribution id for this browser, read from context rather than from
  // storage — `attribution.ts` explains why the resolved id is passed around
  // rather than re-read: storage can be blocked, in which case the minted id
  // lives only in memory for this page load and re-reading would yield nothing.
  //
  // In a REF for the same reason `record` is: it is read inside the flush, and
  // putting it in the effect's dependency array would re-bind the observer
  // (dropping `countedThisVisit`, which inflates re-read counts) the first time
  // it resolves.
  const { leadId } = useLoggerContext();
  const leadIdRef = useRef(leadId);
  useEffect(() => {
    leadIdRef.current = leadId;
  });

  const blockCount = blockIds.length;
  // A stable key for "the same blocks in the same order" — the effect must
  // re-run when a publish changes the chapter under an open reader, and must
  // NOT re-run because a new array with identical contents was rendered.
  //
  // Memoized because this runs on the SCROLL path: `useReadingNav` re-renders
  // the reader roughly once per paragraph crossed, and joining a few hundred
  // ids into a multi-kilobyte string each time is allocation for a value that
  // only changes when the chapter does (review, sales branch).
  const blockKey = useMemo(() => blockIds.join(","), [blockIds]);

  useEffect(() => {
    const container = containerRef.current;
    if (container === null || blockCount === 0) return;

    const indexOf = new Map(blockKey.split(",").map((id, i) => [id, i]));
    const { visibleSince, countedThisVisit } = visit.current;

    const countIfDwelt = (id: string, since: number, now: number): void => {
      if (now - since < DWELL_MS) return;
      if (countedThisVisit.has(id)) return;
      countedThisVisit.add(id);
      const views = pending.current.views;
      views.set(id, (views.get(id) ?? 0) + 1);
      const index = indexOf.get(id);
      if (index !== undefined && index > pending.current.furthest) {
        pending.current.furthest = index;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const now = Date.now();
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).dataset.blockId;
          if (id === undefined) continue;
          // Half the BLOCK, or half the VIEWPORT — whichever a block that tall
          // can actually achieve. See `THRESHOLDS`.
          const fillsView =
            entry.rootBounds !== null &&
            entry.rootBounds.height > 0 &&
            entry.intersectionRect.height >=
              entry.rootBounds.height * VISIBLE_RATIO;
          if (entry.intersectionRatio >= VISIBLE_RATIO || fillsView) {
            if (!visibleSince.has(id)) visibleSince.set(id, now);
            continue;
          }
          // Leaving the viewport settles the visit: count it if it was long
          // enough, then make the block countable again.
          const since = visibleSince.get(id);
          if (since !== undefined) countIfDwelt(id, since, now);
          visibleSince.delete(id);
          countedThisVisit.delete(id);
        }
      },
      { threshold: THRESHOLDS },
    );
    for (const element of container.querySelectorAll<HTMLElement>(
      "[data-block-id]",
    )) {
      observer.observe(element);
    }

    // The dwell tick: blocks that are still on screen and have now been there
    // long enough. Without it, a reader who opens a chapter and reads one
    // paragraph for a minute registers nothing until they scroll.
    const tick = setInterval(() => {
      const now = Date.now();
      for (const [id, since] of visibleSince) countIfDwelt(id, since, now);
    }, TICK_MS);

    const flush = (): void => {
      const { views, furthest } = pending.current;
      if (views.size === 0 && furthest < 0) return;
      pending.current = emptyPending();
      void recordRef
        .current({
          sessionKey: getSessionId(),
          leadId: leadIdRef.current,
          bookSlug,
          unitKey,
          blockCount,
          furthestIndex: furthest,
          blocks: [...views].map(([id, count]) => ({ id, views: count })),
        })
        // Telemetry must never surface to the reader. A dropped flush costs a
        // few observations in a distribution and nothing else.
        .catch(() => undefined);
    };

    const flushTimer = setInterval(flush, FLUSH_MS);
    // `visibilitychange` rather than `beforeunload`: it is the event that
    // actually fires on mobile when a reader switches apps, which is where most
    // sessions end.
    const onHidden = (): void => {
      if (document.visibilityState === "hidden") flush();
    };
    document.addEventListener("visibilitychange", onHidden);

    return () => {
      observer.disconnect();
      clearInterval(tick);
      clearInterval(flushTimer);
      document.removeEventListener("visibilitychange", onHidden);
      // Settle whatever is still on screen, then send: leaving the chapter is
      // the most informative moment there is, and it is exactly when the block
      // a reader stopped on is still visible.
      const now = Date.now();
      for (const [id, since] of visibleSince) countIfDwelt(id, since, now);
      flush();
    };
  }, [containerRef, bookSlug, unitKey, blockKey, blockCount, columnKey]);
}

/**
 * Record that a reader was shown the paywall in place of a unit — the top of
 * the funnel, and the only part of it anonymous readers appear in.
 *
 * There is no "this was locked" flag to send: the server decides whether a row
 * is a read or an impression, from the same access decision that would have
 * served the text (`convex/reading.ts`). This hook and `useReadingTelemetry`
 * therefore send the same shape, and which one a call was is not something a
 * client can assert.
 *
 * One write per mount, not per render. The row is keyed on (session, book,
 * unit), so a reader who returns to the same locked chapter updates one row
 * rather than adding impressions; the console counts distinct sessions
 * alongside raw impressions for exactly that reason.
 */
export function usePaywallImpression({
  bookSlug,
  unitKey,
}: {
  bookSlug: string;
  unitKey: UnitKey;
}): void {
  const record = useMutation(api.reading.record);
  const recordRef = useRef(record);
  useEffect(() => {
    recordRef.current = record;
  });

  // Carried here too: a paywall impression is the top of the funnel, and
  // "which campaign paid for the readers who hit the wall" is the question it
  // exists to answer. In a ref so a resolving id cannot re-fire the write —
  // the row is keyed on (session, book, unit), but a second call would still
  // move `lastAt` and count as another impression.
  const { leadId } = useLoggerContext();
  const leadIdRef = useRef(leadId);
  useEffect(() => {
    leadIdRef.current = leadId;
  });

  useEffect(() => {
    void recordRef
      .current({
        sessionKey: getSessionId(),
        leadId: leadIdRef.current,
        bookSlug,
        unitKey,
        blockCount: 0,
        furthestIndex: -1,
        blocks: [],
      })
      .catch(() => undefined);
  }, [bookSlug, unitKey]);
}
